import React from 'react';
import { useField } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { MediaInterface } from 'types/Media';
import { MediaEntityType, MediaSubType } from 'types/MediaTypeEnum';

import { useSelector } from 'hooks';
import useUpload from 'hooks/useUpload';
import { formatToArray } from 'utils/formUtils';

import { GET_MEDIA_UPLOAD_URL } from 'constants/url';

import { isPublicDevice } from 'modules/dealers/selectors';
import { getSelectedSelfServiceId } from 'modules/auth/selectors';
import { getRepairOrderMediaSupport } from 'modules/refData/selectors';

import Icon from 'components/ui/Icon';
import FileList from 'components/ui/FileList';
import Button, { ButtonType } from 'components/ui/Button';
import FormTextarea from 'components/ui/form/FormTextarea';

const CustomerActions: React.FC = () => {
  const intl = useIntl();

  const isPublic = useSelector(isPublicDevice);
  const selfServiceId = useSelector(getSelectedSelfServiceId);
  const supportedTypes = useSelector(getRepairOrderMediaSupport);

  const {
    value: formMedias,
    onChange: setMedias,
  } = useField('medias', { subscription: { value: true }, format: formatToArray<MediaInterface> }).input;

  const {
    medias, browse, upload, remove, isLoading: isUploading,
  } = useUpload({ selfServiceId, initialMedias: formMedias });

  React.useEffect(() => {
    setMedias(medias);
  }, [setMedias, medias]);

  const handleUpload = React.useCallback(async () => {
    try {
      const files = Array.from(await browse({ multiple: true, accept: supportedTypes }));
      await Promise.all(files.map((file) => upload({
        file,
        filename: file.name,
        contentType: file.type,
        url: GET_MEDIA_UPLOAD_URL,
        subType: MediaSubType.MEDIAS,
        entityType: MediaEntityType.SELF_SERVICE,
      })));
    } catch { /* Do nothing */ }
  }, [browse, upload, supportedTypes]);

  const handleRemove = React.useCallback((media: MediaInterface) => remove(media.uploadId ?? media.id), [remove]);

  return (
    <div className="flex flex-col gap-2 items-center w-full max-w-content">
      <FormattedMessage
        tagName="h2"
        id="repairOrder.remarks"
        defaultMessage="Remarks"
      />
      <FormTextarea
        name="customerComment"
        className="w-full p-8 kiosk:px-10"
        inputClassName="!mt-8 kiosk:!mt-16"
        label={intl.formatMessage({ id: 'repairOrder.comment', defaultMessage: 'Comment' })}
        data-testid="comment"
        description={(
          <FormattedMessage
            id="repairOrder.commentDescription"
            defaultMessage="Specific requests are subject to parts and technician availability"
          />
        )}
        placeholder={intl.formatMessage({
          id: 'repairOrder.commentPlaceholder',
          defaultMessage: 'You can share any additional requests here related to your appointment.'
        })}
      />
      {!isPublic && (
        <Button
          loading={isUploading}
          onClick={handleUpload}
          testId="addAttachments"
          className="w-full"
          type={ButtonType.SECONDARY}
        >
          <div className="flex justify-center !text-button-xs items-center">
            <Icon className="fb-icon--upload mr-xs" />
            <FormattedMessage id="repairOrder.addAttachments" defaultMessage="Add attachments" />
          </div>
        </Button>
      )}
      {medias.length > 0 && (<FileList medias={medias} onDelete={handleRemove} className="w-full mt-5" />)}
    </div>
  );
};

export default CustomerActions;
